import { useEffect, useState, useMemo, useContext } from 'react'
import { TableContext } from 'Context/Table'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import CustomActionCell from './Components/CustomActionCell'
import { useAppStore } from 'Store/App'
import { useSaleOrderStore, INITIAL_STATE as SALE_ORDER_INITIAL_STATE } from 'Store/SaleOrder'
import {
  useSaleOrderItemStore,
  INITIAL_STATE as SALE_ORDER_ITEM_INITIAL_STATE,
} from 'Store/SaleOrderItem'
import { useCustomerStore } from 'Store/Customer'
import { useGeoStore } from 'Store/Geo'
import { shallow } from 'zustand/shallow'
import Swal from 'sweetalert2'
import moment from 'moment'

import { JOB_STATUS } from 'Configuration/JobStatus'
import clsx from 'clsx'
import { toast } from 'react-toastify'

const ViewModel = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { selected, versions, setSelected, getOne, create, update, duplicate, upsertItems } =
    useSaleOrderStore(
      state => ({
        selected: state.selected,
        selectedId: state.selectedId,
        versions: state.versions,
        setSelected: state.setSelected,
        getOne: state.getOne,
        create: state.create,
        update: state.edit,
        duplicate: state.duplicate,
        upsertItems: state.upsertItems,
      }),
      shallow
    )
  // eslint-disable-next-line
  const { addUpdateSaleOrderItem } = useSaleOrderItemStore(
    state => ({
      createSaleOrderItem: state.create,
      updateSaleOrderItem: state.update,
      addUpdateSaleOrderItem: state.addUpdate,
      deleteSaleOrderItem: state.delete,
    }),
    shallow
  )
  const {
    // currentSorting,#

    updateLoading: updateLoadingSaleOrderItem,
    updateError: updateErrorSaleOrderItem,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)

  const setShowBackButton = useAppStore(state => state.setShowBackButton, shallow)
  const INIT_ERROR_ITEMS = {
    itemCode: false,
    itemTitle: false,
    quantity: false,
    actualQuantity: false,
    groupLetter: false,
    remark: false,
    safeLoad: false,
    ps: false,
  }
  const { customers, getAllCustomers, clearStateCustomer } = useCustomerStore(
    state => ({
      customers: state.data,
      getAllCustomers: state.getAll,
      clearStateCustomer: state.clearState,
    }),
    shallow
  )
  const {
    provinces,
    districts,
    subDistricts,
    zipCodes,
    getProvinces,
    getDistricts,
    getSubDistricts,
  } = useGeoStore(state => state, shallow)
  const [hasChanged, setHasChanged] = useState(false)
  const [hasChangedItems, setHasChangedItems] = useState(false)
  const [formState, setFormState] = useState<any>(SALE_ORDER_INITIAL_STATE)
  const [formStateItem, setFormStateItem] = useState<any>(SALE_ORDER_ITEM_INITIAL_STATE)
  const [errors, setErrors] = useState<any>(
    Object.keys(SALE_ORDER_INITIAL_STATE).reduce((acc, key) => ({ ...acc, [key]: false }), {})
  )

  const [errorItem, setErrorItem] = useState<any>(INIT_ERROR_ITEMS)

  const [itemFormType, setItemFormType] = useState<'create' | 'edit'>('create')
  const [showItemForm, setShowItemForm] = useState(false)
  const [disalbedSubmitButton, setDisabledSubmitButton] = useState(false)

  const [isLoadingCustomer, setLoadingCustomer] = useState(false)

  const jobStatusOptions = JOB_STATUS.map(status => ({
    label: `${status.code} - (${status.text})`,
    value: status.code,
  }))
  const provincesOptions = provinces.map(province => ({
    label: `${province.name_th} - (${province.name_en})`,
    value: province.id,
  }))
  const districtOptions = useMemo(() => {
    if (formState.provinceId) {
      return districts
        .filter(district => district.province_id === formState.provinceId)
        .map(district => ({
          label: `${district.name_th} - (${district.name_en})`,
          value: district.id,
        }))
    }
    return []
  }, [districts, formState.provinceId])
  const subDistrictOptions = useMemo(() => {
    if (formState.districtId) {
      return subDistricts
        .filter(subDistrict => subDistrict.amphure_id === formState.districtId)
        .map(subDistrict => ({
          label: `${subDistrict.name_th} - (${subDistrict.name_en})`,
          value: subDistrict.id,
        }))
    }
    return []
  }, [subDistricts, formState.districtId])
  const zipCodeOptions = useMemo(() => {
    if (formState.districtId) {
      return zipCodes
        .filter(zipCode => zipCode.amphure_id === formState.districtId)
        .map(zipCode => ({
          label: `${zipCode.zip_code}`,
          value: zipCode.zip_code,
        }))
        .filter((zipCode, index, self) => self.findIndex(z => z.value === zipCode.value) === index)
    }

    return []
  }, [zipCodes, formState.districtId])
  const zipCodeSelected = useMemo(() => {
    if (provinces.find(p => p.id === formState.provinceId && p.code === '9999')) {
      return '-'
    }

    const item = subDistricts.find(sd => sd.id === formState.subDistrictId)

    if (item) {
      return item.zip_code
    } else {
      return null
    }
  }, [subDistricts, formState.subDistrictId, formState.provinceId, provinces])
  const customerOptions = customers.map((customer: any) => ({
    label: `${customer.title}`,
    value: customer.id,
  }))

  const isOversea = provinces.find(p => p.code === '9999' && p.id === formState.provinceId)

  const { id } = useParams()
  const isCreate = location.pathname.includes('create')
  const isView = location.search.includes('view')

  const [isFocusCustomer, setIsFocusCustomer] = useState(false)

  //////////
  // FORM //
  //////////

  const fetchData = async () => {
    if (isCreate) {
      setFormState(SALE_ORDER_INITIAL_STATE)
      setFormStateItem(SALE_ORDER_ITEM_INITIAL_STATE)
      updateLoadingSaleOrderItem(false)
    } else if (!isCreate && id) {
      updateLoadingSaleOrderItem(true)
      setDisabledSubmitButton(true)
      getOne(parseInt(id)).then(({ success, data }) => {
        if (!success) {
          Swal.fire({
            icon: 'error',
            title: 'Something went wrong',
            text: data,
          })

          updateErrorSaleOrderItem(true)
        } else {
          setFormState({
            ...data.saleOrder,
            deliveryDocumentDate: moment(data.saleOrder.deliveryDocumentDate),
            startJobDate: moment(data.saleOrder.startJobDate),
            endJobDate: moment(data.saleOrder.endJobDate),
          })
          setFormStateItem(data.saleOrder.saleOrderItems)
          updateErrorSaleOrderItem(false)

          getAllCustomers({ q: data.saleOrder.customerTitle })
        }
        updateLoadingSaleOrderItem(false)
        setDisabledSubmitButton(false)
      })
    }

    await getProvinces()
    await getDistricts()
    await getSubDistricts()
  }

  const onSearchCustomer = (val: string, callback: (options: any[]) => void) => {
    if (val.length > 0) {
      setLoadingCustomer(true)
      getAllCustomers({ q: val }).then(({ data, success }) => {
        if (success) {
          setLoadingCustomer(false)
          callback(
            data.map((customer: any) => ({
              label: `${customer.title} (หน่วยงาน: ${customer.division ?? '-'})`,
              value: customer.id,
            }))
          )
        } else {
          setLoadingCustomer(false)
          callback([])
        }
      })
    }
  }

  const onSelectCustomer = (c: any) => {
    const findedCustomer = customers.find((customer: any) => customer.id === c.value)
    if (findedCustomer) {
      setFormState((prevState: any) => ({
        ...prevState,
        customerId: findedCustomer.id,
        customerTitle: findedCustomer.title,
        customerEmail: findedCustomer.email ?? '',
        division: findedCustomer.division ?? '',
        saleTitle: findedCustomer.saleTitle ?? '',
        address: findedCustomer.address ?? '',
        provinceId: findedCustomer.provinceId ?? '',
        districtId: findedCustomer.districtId ?? '',
        subDistrictId: findedCustomer.subDistrictId ?? '',
        postCode: findedCustomer.postCode ?? '',
      }))
    } else {
      setFormState((prevState: any) => ({
        ...prevState,
        customerId: 0,
        customerTitle: c.value,
      }))
    }

    setHasChanged(true)
  }

  const cancelForm = () => {
    navigate('/sale-order')
    setSelected({})
  }

  const onChange = (name: string, value: any) => {
    if (!isView) {
      if (name === 'provinceId') {
        setFormState((prevState: any) => ({
          ...prevState,
          [name]: value,
          districtId: provinces.find(p => p.id === value && p.code === '9999')
            ? 0
            : prevState.districtId,
          subDistrictId: provinces.find(p => p.id === value && p.code === '9999')
            ? 0
            : prevState.subDistrictId,
          postCode: provinces.find(p => p.id === value && p.code === '9999')
            ? ''
            : prevState.postCode,
        }))
      } else {
        setFormState((prevState: any) => ({
          ...prevState,
          [name]: value,
        }))
      }

      setHasChanged(true)
    }
  }
  const onDuplicate = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'คุณแน่ใจหรือไม่?',
      text: 'คุณต้องการทำซ้ำ Sale Order ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      customClass: {
        confirmButton: 'btn btn-sm btn-primary',
        cancelButton: 'btn btn-sm btn-light',
      },
    })
    if (isConfirmed && id) {
      const { success, data } = await duplicate(parseInt(id))
      if (!success) {
        Swal.fire({
          icon: 'error',
          title: 'ทำซ้ำ Sale Order ไม่สำเร็จ',
          text: data,
        })
      } else {
        Swal.fire({
          icon: 'success',
          title: 'ทำซ้ำ Sale Order สำเร็จ',
          text: `Sale Order ID: ${data.id}`,
        }).then(() => {
          navigate(`/sale-order/${data.id}`)
          setHasChanged(false)
          setHasChangedItems(false)
        })
      }
    }
  }

  const onSubmit = async () => {
    if (hasChanged) {
      setDisabledSubmitButton(true)

      const allRequiredFields: string[] = [
        'code',
        'division',
        'saleTitle',
        'customerTitle',
        'deliveryDocumentDate',
        'startJobDate',
        'endJobDate',
        'provinceId',
        'districtId',
        'subDistrictId',
      ]

      // Check Required Fields
      const isError = allRequiredFields.some(key => {
        if (key === 'districtId' || key === 'subDistrictId') {
          if (formState.provinceId === 78) {
            return false
          } else {
            return (
              districtOptions.find(d => d.value === formState.districtId) === undefined ||
              subDistrictOptions.find(sd => sd.value === formState.subDistrictId) === undefined
            )
          }
        } else {
          return formState[key] === '' || !formState[key]
        }
      })

      if (isError) {
        const allErrors: { [key: string]: boolean } = {}

        allRequiredFields.forEach(key => {
          switch (key) {
            case 'postCode':
              allErrors[key] = !zipCodeSelected
              break
            case 'districtId':
            case 'subDistrictId':
              if (formState.provinceId !== 78) {
                const options = key === 'districtId' ? districtOptions : subDistrictOptions
                allErrors[key] = !options.find(option => option.value === formState[key])
              } else {
                allErrors[key] = false
              }
              break
            default:
              allErrors[key] = !formState[key]
              break
          }
        })
        setErrors((prevState: any) => ({
          ...prevState,
          ...allErrors,
        }))
        setDisabledSubmitButton(false)
        return
      } else {
        setErrors((prevState: any) => {
          return {
            ...prevState,
            ...Object.keys(SALE_ORDER_INITIAL_STATE).reduce(
              (acc, key) => ({ ...acc, [key]: false }),
              {}
            ),
          }
        })
      }

      // Perform Submit
      if (isCreate) {
        const body: { [key: string]: any } = {
          code: formState.code,
          division: formState.division,
          saleTitle: formState.saleTitle,
          deliveryDocumentDate: moment(formState.deliveryDocumentDate).format('YYYY-MM-DDTHH:mm:ss'),
          customerTitle: formState.customerTitle,
          customerEmail: formState.customerEmail,
          startJobDate: moment(formState.startJobDate).format('YYYY-MM-DDTHH:mm:ss'),
          endJobDate: moment(formState.endJobDate).format('YYYY-MM-DDTHH:mm:ss'),
          totalDate: formState.totalDate,
          address: formState.address,
          provinceId: formState.provinceId,
          districtId: isOversea ? '' : formState.districtId,
          subDistrictId: isOversea ? '' : formState.subDistrictId,
          postCode: isOversea ? '' : zipCodeSelected,
          // jobStatus: formState.jobStatus,
          jobStatus: JOB_STATUS[0].code,
        }

        create(body).then(({ success, data }) => {
          if (!success) {
            Swal.fire({
              icon: 'error',
              title: 'สร้าง Sale Order ไม่สำเร็จ',
              text: data,
            }).then(() => {
              setDisabledSubmitButton(false)
            })
          } else {
            Swal.fire({
              icon: 'success',
              title: 'สร้าง Sale Order สำเร็จ',
              text: `Sale Order ID: ${data.id}`,
            }).then(() => {
              navigate(`/sale-order`)
              setHasChanged(false)
              setHasChangedItems(false)
            })

            // const id = data.id

            // if (hasChangedItems) {
            //   addUpdateSaleOrderItem(id, formState.saleOrderItems).then(({ success, data }) => {
            //     if (!success) {
            //       Swal.fire({
            //         icon: 'error',
            //         title: 'สร้าง Sale Order ไม่สำเร็จ',
            //         text: data,
            //       }).then(() => {
            //         setDisabledSubmitButton(false)
            //       })
            //     } else {
            //       Swal.fire({
            //         icon: 'success',
            //         title: 'สร้าง Sale Order สำเร็จ',
            //         text: `Sale Order ID: ${id}`,
            //       }).then(() => {
            //         navigate(`/sale-order`)
            //         setHasChanged(false)
            //         setHasChangedItems(false)
            //       })
            //     }
            //   })
            // } else {
            //   Swal.fire({
            //     icon: 'success',
            //     title: 'สร้าง Sale Order สำเร็จ',
            //     text: `Sale Order ID: ${id}`,
            //   }).then(() => {
            //     navigate(`/sale-order`)
            //     setHasChanged(false)
            //   })
            // }
          }
        })
      } else if (!isCreate && id) {
        const body: { [key: string]: any } = {}

        Object.keys(selected).forEach(key => {
          if (key === 'id' || key === 'createdAt' || key === 'updatedAt' || key === 'jobStatus') {
            return
          } else {
            if (key === 'saleOrderDocuments') {
              return
            } else if (key === 'saleOrderItems') {
              return
            } else if (key === 'postCode') {
              if (zipCodeSelected !== selected[key]) {
                body[key] = zipCodeSelected
              }
            } else if (key === 'provinceId') {
              if (formState[key] !== selected[key]) {
                body[key] = formState[key]

                if (isOversea) {
                  body['districtId'] = ''
                  body['subDistrictId'] = ''
                  body['postCode'] = ''
                }
              }
            } else if (['deliveryDocumentDate', 'startJobDate', 'endJobDate'].includes(key)) {
              if (
                moment(formState[key]).format('YYYY-MM-DDTHH:mm:ss') !==
                moment(selected[key]).format('YYYY-MM-DDTHH:mm:ss')
              ) {
                body[key] = moment(formState[key]).format('YYYY-MM-DDTHH:mm:ss')
              }
            } else if (formState[key] !== selected[key]) {
              body[key] = formState[key]
            }
          }
        })

        if (Object.keys(body).length > 0) {
          update(parseInt(id), body).then(({ success, data }) => {
            if (!success) {
              Swal.fire({
                icon: 'error',
                title: 'แก้ไข Sale Order ไม่สำเร็จ',
                text: data,
              }).then(() => {
                setDisabledSubmitButton(false)
              })
            } else {
              Swal.fire({
                icon: 'success',
                title: 'แก้ไข Sale Order สำเร็จ',
                text: `Sale Order ID: ${id}`,
              }).then(() => {
                fetchData()
                // navigate(`/sale-order`)
                setDisabledSubmitButton(false)
                setHasChanged(false)
                setHasChangedItems(false)
              })
              // if (hasChangedItems) {
              //   addUpdateSaleOrderItem(
              //     id,
              //     formState.saleOrderItems.filter((item: any) => !item?.isDeleted)
              //   ).then(({ success, data }) => {
              //     if (!success) {
              //       Swal.fire({
              //         icon: 'error',
              //         title: 'แก้ไข Sale Order ไม่สำเร็จ',
              //         text: data,
              //       }).then(() => {
              //         setDisabledSubmitButton(false)
              //       })
              //     } else {
              //       Swal.fire({
              //         icon: 'success',
              //         title: 'แก้ไข Sale Order สำเร็จ',
              //         text: `Sale Order ID: ${id}`,
              //       }).then(() => {
              //         // fetchData()
              //         navigate(`/sale-order`)
              //         setDisabledSubmitButton(false)
              //         setHasChanged(false)
              //         setHasChangedItems(false)
              //       })
              //     }
              //   })
              // } else {
              //   Swal.fire({
              //     icon: 'success',
              //     title: 'แก้ไข Sale Order สำเร็จ',
              //     text: `Sale Order ID: ${id}`,
              //   }).then(() => {
              //     // fetchData()
              //     navigate(`/sale-order`)
              //     setDisabledSubmitButton(false)
              //   })
              // }
            }
          })
        } else {
          Swal.fire({
            icon: 'success',
            title: 'แก้ไข Sale Order สำเร็จ',
            text: `Sale Order ID: ${id}`,
          }).then(() => {
            fetchData()
            // navigate(`/sale-order`)
            setDisabledSubmitButton(false)
            setHasChanged(false)
            setHasChangedItems(false)
          })
          // if (hasChangedItems) {
          //   addUpdateSaleOrderItem(
          //     id,
          //     formState.saleOrderItems.filter((item: any) => !item?.isDeleted)
          //   ).then(({ success, data }) => {
          //     if (!success) {
          //       Swal.fire({
          //         icon: 'error',
          //         title: 'แก้ไข Sale Order ไม่สำเร็จ',
          //         text: data,
          //       }).then(() => {
          //         setDisabledSubmitButton(false)
          //       })
          //     } else {
          //       Swal.fire({
          //         icon: 'success',
          //         title: 'แก้ไข Sale Order สำเร็จ',
          //         text: `Sale Order ID: ${id}`,
          //       }).then(() => {
          //         // fetchData()
          //         navigate(`/sale-order`)
          //         setDisabledSubmitButton(false)
          //         setHasChanged(false)
          //         setHasChangedItems(false)
          //       })
          //     }
          //   })
          // }
        }
      }
    }
  }

  ///////////
  // ITEMS //
  ///////////

  const setupTable = () => {
    updateSorting('sequence', false)
    updatePagination(true)
  }

  const closeItemForm = () => {
    setShowItemForm(false)
    setFormStateItem(SALE_ORDER_ITEM_INITIAL_STATE)
    setItemFormType('create')
    setErrorItem(INIT_ERROR_ITEMS)
  }

  const onChangeItem = (name: string, value: any) => {
    if (!isView) {
      console.log('name', name, 'value', value)
      setFormStateItem((prevState: any) => ({
        ...prevState,
        [name]: value,
      }))

      setHasChangedItems(true)
    }
  }

  const createItem = () => {
    setHasChangedItems(false)
    setItemFormType('create')
    setShowItemForm(true)

    const newState: { [key: string]: any } = SALE_ORDER_ITEM_INITIAL_STATE
    if (formState.saleOrderItems.length > 0) {
      newState.sequence = formState.saleOrderItems[formState.saleOrderItems.length - 1].sequence + 1
    }

    setFormStateItem(newState)
  }

  const editItem = (id: string) => {
    setItemFormType('edit')
    setHasChangedItems(false)

    const item = formState.saleOrderItems.find((item: any) => item.id === id)
    if (item) {
      setFormStateItem(item)
    }

    setShowItemForm(true)
  }

  const deleteItemFromCode = async (itemCode: string) => {
    if (isCreate) {
      let items = formState.saleOrderItems
      const index = items.findIndex((item: any) => item.itemCode === itemCode)

      items = items.filter((item: any) => item.itemCode !== itemCode)
      if (index !== -1) {
        items = items.map((item: any, i: number) => {
          if (i >= index) {
            return {
              ...item,
              sequence: item.sequence - 1,
            }
          } else {
            return item
          }
        })
      }

      setFormState((prevState: any) => ({
        ...prevState,
        saleOrderItems: items,
      }))
    } else if (!isCreate && id) {
      setFormState((prevState: any) => ({
        ...prevState,
        saleOrderItems: prevState.saleOrderItems.map((item: any) => {
          if (item.itemCode === itemCode) {
            return {
              ...item,
              isDeleted: true,
            }
          }

          return item
        }),
      }))

      setHasChanged(true)
      setHasChangedItems(true)
    }
  }

  const deleteItem = async (itemId: string) => {
    if (isCreate) {
      setFormState((prevState: any) => ({
        ...prevState,
        saleOrderItems: prevState.saleOrderItems.filter((item: any) => item.id !== itemId),
      }))
    } else if (!isCreate && id) {
      let items = formState.saleOrderItems
      const index = items.findIndex((item: any) => item.id === itemId)

      items = items.map((item: any, i: number) => {
        if (i > index) {
          return {
            ...item,
            sequence: item.sequence - 1,
          }
        } else if (i === index) {
          return {
            ...item,
            isDeleted: true,
          }
        } else {
          return item
        }
      })

      setFormState((prevState: any) => ({
        ...prevState,
        saleOrderItems: items,
      }))

      setHasChanged(true)
      setHasChangedItems(true)

      // const { isConfirmed } = await Swal.fire({
      //   title: 'คุณแน่ใจหรือไม่?',
      //   text: 'คุณต้องการลบรายการเสานี้ใช่หรือไม่?',
      //   icon: 'warning',
      //   showCancelButton: true,
      //   confirmButtonText: 'ยืนยัน',
      //   cancelButtonText: 'ยกเลิก',
      //   customClass: {
      //     confirmButton: 'btn btn-sm btn-primary',
      //     cancelButton: 'btn btn-sm btn-light',
      //   },
      // })
      // if (isConfirmed) {
      //   const { success, data } = await addUpdateSaleOrderItem(
      //     id,
      //     formState.saleOrderItems.filter((item: any) => item.id !== itemId)
      //   )
      //   if (success) {
      //     toast.success('ลบรายการเสาสำเร็จ')
      //     fetchData()
      //   } else {
      //     toast.error(data)
      //   }
      // }
    }
  }

  const cancelDeleteItem = (itemId: string) => {
    let items = formState.saleOrderItems
    const index = items.findIndex((item: any) => item.id === itemId)

    items = items.map((item: any, i: number) => {
      if (i === index) {
        return {
          ...item,
          isDeleted: undefined,
        }
      } else if (i > index) {
        return {
          ...item,
          sequence: item.sequence + 1,
        }
      }

      return item
    })

    setFormState((prevState: any) => ({
      ...prevState,
      saleOrderItems: items,
    }))
  }

  const onSubmitItem = async () => {
    if (hasChangedItems) {
      const allRequiredFields = [
        'itemCode',
        'itemTitle',
        'quantity',
        'sequence',
        'actualQuantity',
        // 'groupLetter',
        'hammerMethod',
      ]
      if (allRequiredFields.some(field => !formStateItem[field] || formStateItem[field] === '')) {
        allRequiredFields.forEach(field => {
          if (!formStateItem[field] || formStateItem[field] === '') {
            setErrorItem((prevState: any) => ({
              ...prevState,
              [field]: true,
            }))
          } else {
            setErrorItem((prevState: any) => ({
              ...prevState,
              [field]: false,
            }))
          }
        })
        Swal.fire({
          icon: 'error',
          title: 'Something went wrong',
          text: 'Please fill in all required fields',
        })
        console.log(errorItem)
        return
      }
      const { success, data } = await upsertItems(selected.id, formStateItem)
      if (success) {
        toast.success('เพิ่มรายการสำเร็จ')
        fetchData()
      } else {
        toast.error(data)
      }
      setHasChanged(true)
      setHasChangedItems(true)
      closeItemForm()
    }
  }

  const ITEMS_COLUMNS_CONFIG: any[] = [
    {
      Header: 'No.',
      accessor: 'sequence',
      width: 80,
      minWidth: 80,
      Cell: ({ value, row }: any) => {
        return (
          <span
            className={clsx({
              'text-decoration-line-through text-danger': row.original.isDeleted,
              'text-gray-800': !row.original.isDeleted,
            })}>
            {value}
          </span>
        )
      },
    },
    {
      Header: 'Code',
      accessor: 'itemCode',
      width: 80,
      minWidth: 80,
      Cell: ({ value, row }: any) => {
        return (
          <span
            className={clsx({
              'text-decoration-line-through text-danger': row.original.isDeleted,
              'text-gray-800': !row.original.isDeleted,
            })}>
            {value}
          </span>
        )
      },
    },
    {
      Header: 'ชื่อรายการ',
      accessor: 'itemTitle',
      width: 80,
      minWidth: 80,
      Cell: ({ value, row }: any) => {
        return (
          <span
            className={clsx({
              'text-decoration-line-through text-danger': row.original.isDeleted,
              'text-gray-800': !row.original.isDeleted,
            })}>
            {value}
          </span>
        )
      },
    },
    {
      Header: 'จำนวน (ท่อน)',
      accessor: 'quantity',
      width: 80,
      minWidth: 80,
      Cell: ({ value, row }: any) => {
        return (
          <span
            className={clsx({
              'text-decoration-line-through text-danger': row.original.isDeleted,
              'text-gray-800': !row.original.isDeleted,
            })}>
            {value}
          </span>
        )
      },
    },
    {
      Header: 'ความยาวใช้จริง',
      accessor: 'actualQuantity',
      width: 80,
      minWidth: 80,
      Cell: ({ value, row }: any) => {
        return (
          <span
            className={clsx({
              'text-decoration-line-through text-danger': row.original.isDeleted,
              'text-gray-800': !row.original.isDeleted,
            })}>
            {value}
          </span>
        )
      },
    },
    // {
    //   Header: 'Group',
    //   accessor: 'groupLetter',
    //   width: 80,
    //   minWidth: 80,
    //   Cell: ({ value, row }: any) => {
    //     return (
    //       <span
    //         className={clsx({
    //           'text-decoration-line-through text-danger': row.original.isDeleted,
    //           'text-gray-800': !row.original.isDeleted,
    //         })}>
    //         {value}
    //       </span>
    //     )
    //   },
    // },
    {
      Header: 'โรงงาน',
      accessor: 'factory',
      width: 80,
      minWidth: 80,
      Cell: ({ value, row }: any) => {
        return (
          <span
            className={clsx({
              'text-decoration-line-through text-danger': row.original.isDeleted,
              'text-gray-800': !row.original.isDeleted,
            })}>
            {value}
          </span>
        )
      },
    },
    {
      Header: 'หมายเหตุ',
      accessor: 'remark',
      width: 100,
      minWidth: 100,
      Cell: ({ value, row }: any) => {
        return (
          <span
            className={clsx({
              'text-decoration-line-through text-danger': row.original.isDeleted,
              'text-gray-800': !row.original.isDeleted,
            })}>
            {value}
          </span>
        )
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 100,
      minWidth: 100,
      Cell: ({ row }: any) => (
        <div className='text-end'>
          <CustomActionCell
            id={row.original.id}
            hideView
            hideDuplicate
            hideDelete={row.original.isDeleted}
            hideCancelDeleted={!row.original.isDeleted}
            onClickEdit={() => editItem(row.original.id)}
            onClickDelete={() =>
              isCreate ? deleteItemFromCode(row.original.itemCode) : deleteItem(row.original.id)
            }
            onClickCancelDeleted={() => cancelDeleteItem(row.original.id)}
          />
        </div>
      ),
    },
  ]

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    setupTable()
    setShowBackButton(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset Hook
  useEffect(() => {
    return () => {
      resetTableState()
      setFormState(SALE_ORDER_INITIAL_STATE)
      setFormStateItem(SALE_ORDER_ITEM_INITIAL_STATE)
      setShowBackButton(false)
      clearStateCustomer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isView,
    isCreate,
    isOversea,
    isLoadingCustomer,
    jobStatusOptions,
    handleSubmit: onSubmit,
    cancel: cancelForm,
    errors,
    formState,
    onChange,
    zipCodeSelected,
    formStateItem,
    itemsData: formState.saleOrderItems,
    versions,
    onChangeItem,
    provincesOptions,
    districtOptions,
    subDistrictOptions,
    zipCodeOptions,
    customerOptions,
    showItemForm,
    closeItemForm,
    ITEMS_COLUMNS_CONFIG,
    createItem,
    itemFormType,
    onSubmitItem,
    upsertItems,
    onDuplicate,
    errorItem,
    disalbedSubmitButton,
    isFocusCustomer,
    setIsFocusCustomer,
    onSearchCustomer,
    onSelectCustomer,
  }
}

export default ViewModel
